import * as React from "react";
import { cn } from "../utils";

/**
 * RowShell — THE interactive list-row primitive (docs/design/VERTICAL-RHYTHM-PRD.md
 * §T3, decision D3).
 *
 * Before this existed, "a row in a list" was re-declared privately by ~10 blocks
 * at four vertical paddings (py-1.5/2/2.5/3) and four left insets (px-2/3/4/6) —
 * Home's Signals list (py-2.5) and the review queues (py-3) disagreed by 4px per
 * row for the same concept (PRD R5). RowShell owns the one rhythm so a list can't
 * drift by construction: a hairline divider, vertical padding from the
 * `--row-py` token (globals.css @theme, default 10px — the D3 value), the crowned
 * px-4 gutter, and the hover affordance. As the primitive layer it is the one
 * place allowed to own the interactive row treatment (CLAUDE.md Hard Rule 10).
 *
 * Tables are NOT built on this — table cells stay `py-2` (the deliberately denser
 * tier) and their `px-4` is load-bearing for the 17px Ledger hang. FeedList,
 * TaskList, SelectableRow, SettingsRow, and the ListCard row pattern compose
 * RowShell (or its metrics where their markup — a `<button>`, an `<li>` link —
 * doesn't fit a bare wrapper `<div>`).
 */
export interface RowShellProps extends React.ComponentProps<"div"> {
  /** Row reacts to hover (list rows that open something). Default false. */
  interactive?: boolean;
  /** Own the horizontal gutter (px-4). Pass false when the parent owns it. */
  gutter?: boolean;
}

export function RowShell({
  interactive = false,
  gutter = true,
  className,
  children,
  ...props
}: RowShellProps) {
  return (
    <div
      data-slot="row-shell"
      className={cn(
        // No first:pt-0 (deliberately superseding FeedList's old top-trim):
        // equal padding top and bottom keeps the header→row gap and the
        // row→row gap on one rhythm (VERTICAL-RHYTHM-PRD §T3).
        "flex items-center gap-3 border-b border-border/40 py-[var(--row-py)] last:border-b-0",
        gutter && "px-[var(--content-gutter)]",
        interactive && "transition-colors hover:bg-selected",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
